/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Calendar } from '../housien-models/Calendar';
import type {
  CalendarInput,
  CalendarUpdateInput,
} from '../housien-models/CalendarInput';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';
import type { Paginated } from './CalendarService.types';

/**
 * List all calendars of an agency.
 * @param agencyId The agency id.
 */
export function listCalendars(
  agencyId: string
): CancelablePromise<Paginated<Calendar>> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/calendar/calendars',
    path: { agencyId },
  });
}

/**
 * Fetch a single calendar by id.
 * @param agencyId The agency id.
 * @param calendarId The calendar id.
 */
export function getCalendar(
  agencyId: string,
  calendarId: string
): CancelablePromise<Calendar> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/calendar/calendars/{calendarId}',
    path: { agencyId, calendarId },
  });
}

/**
 * Create a calendar (personal or institutional).
 * @param agencyId The agency id.
 * @param requestBody The calendar payload.
 */
export function createCalendar(
  agencyId: string,
  requestBody: CalendarInput
): CancelablePromise<{ calendar_id: string }> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/calendar/calendars',
    path: { agencyId },
    body: requestBody,
    mediaType: 'application/json',
  });
}

/**
 * Update a calendar.
 * @param agencyId The agency id.
 * @param calendarId The calendar id.
 * @param requestBody The fields to update.
 */
export function updateCalendar(
  agencyId: string,
  calendarId: string,
  requestBody: CalendarUpdateInput
): CancelablePromise<Calendar> {
  return __request(OpenAPIHousien, {
    method: 'PUT',
    url: '/agency/{agencyId}/calendar/calendars/{calendarId}',
    path: { agencyId, calendarId },
    body: requestBody,
    mediaType: 'application/json',
  });
}

/**
 * Start the Google Calendar OAuth connect flow.
 *
 * Requests JSON (rather than following the 302) so the SPA can attach its
 * Bearer token; the backend returns the Google `authorization_url` to which
 * the caller should then navigate.
 * @param agencyId The agency id.
 */
export function connectGoogle(
  agencyId: string
): CancelablePromise<{ authorization_url: string }> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/calendar/google/connect',
    path: { agencyId },
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
}

/**
 * Incrementally refresh Google Calendar events into Housien.
 * @param agencyId The agency id.
 */
export function syncGoogle(
  agencyId: string
): CancelablePromise<{ imported_count: number; sync_status: string }> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/calendar/google/sync',
    path: { agencyId },
  });
}

/** The connected Google account's calendars and the user's push choices. */
export interface GoogleCalendarSettings {
  google_calendars: {
    id: string;
    name: string;
    color: string;
    access_role: string;
    imported: boolean;
  }[];
  destination_calendar_id: string;
  push_personal_calendar_ids: string[];
  push_personal_events: boolean;
  push_visits: boolean;
  push_edits_to_imported: boolean;
}

/** The fields a client may write, including which calendars are mirrored. */
export type GoogleSettingsWritable = Partial<
  Omit<GoogleCalendarSettings, 'google_calendars'>
> & { import_calendar_ids?: string[] };

/**
 * Read the signed-in user's Google Calendar settings.
 * @param agencyId The agency id.
 */
export function getGoogleSettings(
  agencyId: string
): CancelablePromise<GoogleCalendarSettings> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/calendar/google/settings',
    path: { agencyId },
  });
}

/**
 * Apply a partial change to the Google Calendar settings.
 * @param agencyId The agency id.
 * @param patch The fields to change.
 */
export function updateGoogleSettings(
  agencyId: string,
  patch: GoogleSettingsWritable
): CancelablePromise<GoogleSettingsWritable> {
  return __request(OpenAPIHousien, {
    method: 'PATCH',
    url: '/agency/{agencyId}/calendar/google/settings',
    path: { agencyId },
    body: patch,
    mediaType: 'application/json',
  });
}

/**
 * Disconnect Google Calendar for the authenticated user.
 * @param agencyId The agency id.
 */
export function disconnectGoogle(agencyId: string): CancelablePromise<void> {
  return __request(OpenAPIHousien, {
    method: 'DELETE',
    url: '/agency/{agencyId}/calendar/google/connect',
    path: { agencyId },
  });
}

/**
 * Delete a calendar.
 * @param agencyId The agency id.
 * @param calendarId The calendar id.
 */
export function deleteCalendar(
  agencyId: string,
  calendarId: string
): CancelablePromise<void> {
  return __request(OpenAPIHousien, {
    method: 'DELETE',
    url: '/agency/{agencyId}/calendar/calendars/{calendarId}',
    path: { agencyId, calendarId },
  });
}
